// crewbit-admin/src/components/money/clients/PolymorphicLateFeeEditor.tsx
//
// Tier 2-UI Phase D-admin §WorldClass.1 — paired editor for
// `clients.default_late_fee_policy` + `clients.default_late_fee_amount`.
// Mirrors crewbit-mobile/components/money/clients/PolymorphicLateFeeEditor.tsx
// body decisions; web idiom uses native <select> + <input type="number">
// instead of RN SegmentedControl + TextInput.
//
// Migration 275 added a polymorphic CHECK: when policy='percent' amount must
// be 0..100; when policy='flat' amount must be > 0; when policy IS NULL
// amount must IS NULL. UI mirrors the CHECK by:
//   - <select> sets policy + clears amount on cross-state transition
//     (prevents CHECK violation on save)
//   - <input> affix flips between '%' / '$' based on policy
//   - 'none' policy hides the amount input + nulls both fields

import { palette, radii, spacing, typography } from '../../../styles/tokens';
import type { LateFeePolicy } from '../../../types/clientDefaults';

type Mode = 'none' | 'percent' | 'flat';

interface Props {
  policy: LateFeePolicy | null;
  amount: number | null;
  onChange: (next: { policy: LateFeePolicy | null; amount: number | null }) => void;
  disabled?: boolean;
}

export default function PolymorphicLateFeeEditor({ policy, amount, onChange, disabled }: Props) {
  const mode: Mode = policy ?? 'none';

  const handleModeChange = (next: Mode) => {
    if (next === mode) return;
    if (next === 'none') {
      onChange({ policy: null, amount: null });
    } else {
      // Cross-state transition (e.g. percent → flat) clears amount —
      // amounts in different units shouldn't carry over without re-entry.
      onChange({ policy: next, amount: null });
    }
  };

  const handleAmountChange = (raw: string) => {
    if (!policy) return;
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      onChange({ policy, amount: null });
      return;
    }
    const parsed = Number.parseFloat(trimmed);
    if (Number.isNaN(parsed)) return;
    onChange({ policy, amount: parsed });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
      <select
        value={mode}
        onChange={(e) => handleModeChange(e.target.value as Mode)}
        disabled={disabled}
        style={{
          background: palette.white,
          border: `1px solid ${palette.g200}`,
          borderRadius: radii.sm,
          paddingInline: spacing.md,
          paddingBlock: spacing.sm,
          fontSize: typography.size.body,
          fontFamily: typography.fontFamily,
          color: palette.g800,
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <option value="none">None</option>
        <option value="percent">Percent</option>
        <option value="flat">Flat</option>
      </select>

      {policy && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
            background: palette.white,
            border: `1px solid ${palette.g200}`,
            borderRadius: radii.sm,
            paddingInline: spacing.md,
          }}
        >
          {policy === 'flat' && (
            <span style={{ color: palette.g600, fontSize: typography.size.body }}>$</span>
          )}
          <input
            type="number"
            value={amount === null ? '' : String(amount)}
            onChange={(e) => handleAmountChange(e.target.value)}
            placeholder={policy === 'percent' ? '1.5' : '25.00'}
            step="0.01"
            min={policy === 'percent' ? 0 : 0.01}
            max={policy === 'percent' ? 100 : undefined}
            disabled={disabled}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: typography.size.body,
              fontFamily: typography.fontFamily,
              color: disabled ? palette.g400 : palette.g800,
              paddingBlock: spacing.sm,
            }}
          />
          {policy === 'percent' && (
            <span style={{ color: palette.g600, fontSize: typography.size.body }}>%</span>
          )}
        </div>
      )}

      {policy && (
        <div style={{ fontSize: typography.size.caption, color: palette.g500 }}>
          Late fee policy and amount must match
        </div>
      )}
    </div>
  );
}
